<template>
  <div
    ref="rootEl"
    class="header-theme"
    :class="{
      'header-theme--nav-mid': variant === 'nav-mid',
      'header-theme--nav-mid-light': variant === 'nav-mid-light',
    }"
  >
    <button
      type="button"
      class="header-theme__toggle"
      aria-label="Color theme"
      :aria-expanded="open"
      aria-haspopup="listbox"
      aria-controls="header-theme-list"
      @click.stop="open = !open"
    >
      <i class="fas fa-palette" aria-hidden="true" />
    </button>
    <transition name="header-theme-fade">
      <div v-if="open" id="header-theme-list" class="header-theme__dropdown" role="listbox">
        <div class="header-theme__hint">Colors match across the shop</div>
        <button
          v-for="t in themeList"
          :key="t.id"
          type="button"
          class="header-theme__row"
          :class="{ 'header-theme__row--active': currentFrontendThemeId === t.id }"
          role="option"
          :aria-selected="currentFrontendThemeId === t.id"
          @click="select(t.id)"
        >
          <span class="header-theme__dots" aria-hidden="true">
            <span class="header-theme__dot" :style="{ background: t.secondary }" />
            <span class="header-theme__dot" :style="{ background: t.accent }" />
            <span class="header-theme__dot" :style="{ background: t.secondaryLight }" />
          </span>
          <span class="header-theme__name">{{ t.label }}</span>
        </button>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import {
  FRONTEND_THEMES,
  applyFrontendTheme,
  currentFrontendThemeId,
} from '@/utils/frontend-theme.js';

// Compiler macro in script setup
// eslint-disable-next-line no-undef
defineProps({
  variant: {
    type: String,
    default: 'default',
    validator: (v) => ['default', 'nav-mid', 'nav-mid-light'].includes(v),
  },
});

const rootEl = ref(null);
const open = ref(false);

const themeList = computed(() => Object.values(FRONTEND_THEMES));

function select(id) {
  applyFrontendTheme(id);
  open.value = false;
}

function onDocClick(e) {
  const el = rootEl.value;
  if (!el || el.contains(e.target)) return;
  open.value = false;
}

onMounted(() => {
  document.addEventListener('click', onDocClick);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick);
});
</script>

<style scoped>
.header-theme {
  position: relative;
  display: inline-block;
  vertical-align: middle;
}

.header-theme__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 0.2rem 0.45rem;
  margin: 0;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;
}

.header-theme__toggle:hover,
.header-theme__toggle:focus-visible {
  outline: none;
  border-color: var(--fe-accent, #fcbe00);
  color: var(--fe-accent, #fcbe00);
  background: rgba(255, 255, 255, 0.12);
}

.header-theme__dropdown {
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  min-width: 220px;
  max-height: min(70vh, 420px);
  overflow-y: auto;
  padding: 0.5rem 0;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  border: 1px solid #e8e8e8;
  z-index: 10050;
}

.header-theme__hint {
  padding: 0.35rem 0.85rem 0.5rem;
  font-size: 11px;
  color: #888;
  line-height: 1.3;
  border-bottom: 1px solid #eee;
  margin-bottom: 0.25rem;
}

.header-theme__row {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  padding: 0.45rem 0.85rem;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-size: 13px;
  color: #333;
  transition: background 0.15s ease;
}

.header-theme__row:hover {
  background: var(--fe-accent-soft, rgba(0, 0, 0, 0.04));
}

.header-theme__row--active {
  background: color-mix(in srgb, var(--fe-accent, #fcbe00) 12%, #fff);
  font-weight: 600;
}

.header-theme__dots {
  display: inline-flex;
  gap: 4px;
  flex-shrink: 0;
}

.header-theme__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.header-theme__name {
  flex: 1;
  line-height: 1.25;
}

.header-theme-fade-enter-active,
.header-theme-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.header-theme-fade-enter-from,
.header-theme-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Align with .header-action .icon-link (profile / cart row) */
.header-theme--nav-mid .header-theme__toggle {
  padding: 0.25rem 0.5rem;
  min-height: 44px;
}

.header-theme--nav-mid .header-theme__toggle i {
  font-size: 22px;
  line-height: 1;
  vertical-align: middle;
}

.header-theme--nav-mid .header-theme__dropdown {
  right: 0;
  left: auto;
}

/* White shop header row — compact pill, matches HeaderOne “premium” row */
.header-theme--nav-mid-light .header-theme__toggle {
  min-height: 42px;
  padding: 0.35rem 0.55rem;
  gap: 0;
  color: var(--fe-text-on-light, #3d4a5c);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}

.header-theme--nav-mid-light .header-theme__toggle i {
  font-size: 15px;
  line-height: 1;
  opacity: 0.9;
}

.header-theme--nav-mid-light .header-theme__toggle:hover,
.header-theme--nav-mid-light .header-theme__toggle:focus-visible {
  outline: none;
  border-color: var(--fe-accent, #fcbe00);
  color: var(--fe-secondary, #263c97);
  background: #fff;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.08), 0 0 0 2px var(--fe-accent-soft, rgba(252, 190, 0, 0.2));
}

.header-theme--nav-mid-light .header-theme__dropdown {
  border-radius: 12px;
  border: 1px solid #e8ecf1;
  box-shadow: 0 16px 48px rgba(15, 23, 42, 0.12);
}
</style>